/**
 * iter233 · Production slot editor — create / edit / duplicate / move.
 *
 * Carries the user's batch maths: enter a target quantity and the sheet
 * shows exact batches, the nearest full batches, the resulting output and
 * the excess. The Factory Head decides; nothing is silently rounded.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { fmtQty } from '../../utils/format';
import { SHIFTS, PRIORITIES, canEditField, isFactoryHead } from './planningColumns';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PURPOSES = [
  { v: 'customer_order', l: 'Customer Order' },
  { v: 'stock_replenishment', l: 'Stock Replenishment' },
  { v: 'buffer_stock', l: 'Buffer Stock' },
  { v: 'forecast', l: 'Forecast Production' },
  { v: 'management_decision', l: 'Management Decision' },
  { v: 'other', l: 'Trial / R&D / Other' },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function PlanSlotModal({ mode, seed = {}, role, onClose, onSaved }) {
  const [f, setF] = useState({
    planned_date: seed.planned_date || todayISO(),
    shift: seed.shift || 'day',
    line_no: seed.line_no || '',
    product_id: seed.product_id || '',
    product_name: seed.product_name || '',
    product_code: seed.product_code || '',
    party_code: seed.party_code || '',
    pack_size: seed.pack_size || '',
    customer_id: seed.customer_id || '',
    customer_name: seed.customer_name || '',
    purpose: seed.purpose || (seed.customer_name ? 'customer_order' : 'stock_replenishment'),
    sales_order_id: seed.sales_order_id || '',
    sales_order_number: seed.sales_order_number || '',
    required_date: (seed.required_date || '').slice(0, 10),
    priority: seed.priority || 'normal',
    batch_size: seed.batch_size || '',
    batches: seed.batches || '',
    quantity: seed.quantity ?? seed.total_planned ?? seed.needs_qty ?? '',
    instructions: seed.instructions || '',
    remarks: seed.remarks || '',
  });
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [ruleCheck, setRuleCheck] = useState(null);

  const isNew = mode === 'new' || mode === 'from-requirement';
  const fh = isFactoryHead(role);

  useEffect(() => {
    if (mode !== 'new') return;
    axios.get(`${API}/inventory/finished-goods`, { withCredentials: true })
      .then(r => setProducts(r.data || [])).catch(() => {});
  }, [mode]);

  // Pull the standard batch size from the recipe when we don't have one yet.
  useEffect(() => {
    if (f.batch_size || (!f.product_id && !f.product_name)) return;
    axios.get(`${API}/factory-plans/batch-hint`, {
      params: { product_id: f.product_id || undefined, product_name: f.product_name || undefined, quantity: f.quantity || 0 },
      withCredentials: true,
    }).then(r => {
      if (r.data?.batch_size) setF(x => ({ ...x, batch_size: r.data.batch_size }));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.product_id, f.product_name]);

  const calc = useMemo(() => {
    const bs = parseFloat(f.batch_size) || 0;
    const qty = parseFloat(f.quantity) || 0;
    if (!bs || !qty) return null;
    const exact = qty / bs;
    const full = Math.ceil(exact - 0.0001);
    return { bs, qty, exact: Math.round(exact * 100) / 100, full, output: Math.round(full * bs * 1000) / 1000, excess: Math.round((full * bs - qty) * 1000) / 1000 };
  }, [f.batch_size, f.quantity]);

  const set = (k, v) => setF(x => ({ ...x, [k]: v }));

  // iter235 — live manufacturing-rule check. Warns, never blocks.
  useEffect(() => {
    if (!f.product_name && !f.product_id) { setRuleCheck(null); return; }
    const t = setTimeout(() => {
      axios.post(`${API}/manufacturing-rules/check-slot`, {
        id: (mode === 'new' || mode === 'from-requirement') ? '' : seed.id,
        planned_date: f.planned_date, shift: f.shift, line_no: f.line_no,
        product_name: f.product_name, product_id: f.product_id,
        quantity: parseFloat(f.quantity) || 0, batches: parseInt(f.batches || 0, 10) || 0,
        remarks: f.remarks, instructions: f.instructions,
      }, { withCredentials: true })
        .then(r => setRuleCheck(r.data)).catch(() => setRuleCheck(null));
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.planned_date, f.shift, f.line_no, f.product_name, f.product_id, f.quantity,
      f.batches, f.remarks, f.instructions]);

  const applyBatches = (n) => {
    const bs = parseFloat(f.batch_size) || 0;
    setF(x => ({ ...x, batches: n, quantity: bs ? Math.round(n * bs * 1000) / 1000 : x.quantity }));
  };

  const save = async () => {
    if (!f.product_name && !f.product_id) { toast.error('Pick a product'); return; }
    if (!parseFloat(f.quantity)) { toast.error('Enter a quantity (kg)'); return; }
    setSaving(true);
    try {
      let created = null;
      if (isNew) {
        const res = await axios.post(`${API}/factory-plans/from-requirement`, {
          ...f,
          quantity: parseFloat(f.quantity),
          batches: parseInt(f.batches || 0, 10) || 0,
          batch_size: parseFloat(f.batch_size) || 0,
        }, { withCredentials: true });
        created = res.data;
        toast.success('Production slot created');
      } else if (mode === 'duplicate') {
        const res = await axios.post(`${API}/factory-plans/${seed.id}/duplicate`, {
          planned_date: f.planned_date, shift: f.shift, line_no: f.line_no,
          quantity: parseFloat(f.quantity), batches: parseInt(f.batches || 0, 10) || 0,
        }, { withCredentials: true });
        created = res.data;
        toast.success('Slot duplicated');
      } else if (mode === 'move') {
        await axios.post(`${API}/factory-plans/${seed.id}/move`, { planned_date: f.planned_date }, { withCredentials: true });
        toast.success('Slot moved');
      } else {
        const patch = {};
        const editable = ['planned_date', 'shift', 'line_no', 'batches', 'priority',
          'purpose', 'instructions', 'remarks', 'required_date', 'pack_size',
          'batch_size', 'product_code', 'party_code', 'customer_name'];
        editable.forEach(k => {
          const before = seed[k] ?? '';
          const now = f[k] ?? '';
          if (String(before) !== String(now) && canEditField(role, k)) {
            patch[k] = ['batches'].includes(k) ? parseInt(now || 0, 10)
              : ['batch_size'].includes(k) ? parseFloat(now || 0) : now;
          }
        });
        const qty = parseFloat(f.quantity);
        if (qty !== parseFloat(seed.total_planned ?? seed.quantity ?? 0)) patch.quantity = qty;
        if (Object.keys(patch).length === 0) { toast.info('Nothing changed'); onClose(); return; }
        await axios.patch(`${API}/factory-plans/${seed.id}`, patch, { withCredentials: true });
        toast.success('Slot updated');
      }
      onSaved?.(created);
      onClose();
      const warns = ruleCheck?.warnings || [];
      if (warns.length) {
        toast.warning(`Saved · ${warns.length} manufacturing rule warning: ${warns[0].text}`,
          { duration: 8000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };

  const title = { new: 'New production slot', 'from-requirement': 'Plan this requirement',
    edit: 'Edit production slot', duplicate: 'Duplicate slot', move: 'Move slot' }[mode];

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="ti-surface-modal border border-zinc-800 rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-auto"
        onClick={e => e.stopPropagation()} data-testid="fp-slot-modal">
        <div className="sticky top-0 ti-surface-modal border-b border-zinc-800 px-5 py-3 flex justify-between items-center">
          <div>
            <p className="font-heading text-sm text-zinc-50">{title}</p>
            <p className="text-[11px] text-zinc-500">
              {f.product_name || 'product'}{f.customer_name ? ` · ${f.customer_name}` : ''}
              {seed.sales_order_number ? ` · SO ${seed.sales_order_number}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-100 text-lg">×</button>
        </div>

        <div className="p-5 space-y-4">
          {(ruleCheck?.flags || []).length > 0 && (
            <div className={`rounded-xl border px-3 py-2.5 space-y-1 ${(ruleCheck.warnings || []).length
              ? 'bg-red-500/[0.09] border-red-500/40' : 'bg-zinc-800/60 border-zinc-700'}`}
              data-testid="fp-slot-rule-banner">
              <p className={`text-[11px] font-semibold ${(ruleCheck.warnings || []).length ? 'text-red-200' : 'text-zinc-300'}`}>
                {(ruleCheck.warnings || []).length
                  ? `⚠ This slot breaks ${ruleCheck.warnings.length} manufacturing rule — you can still save it`
                  : 'Rule notes for this slot'}
              </p>
              {ruleCheck.flags.map((fl, i) => (
                <p key={i} className={`text-[11px] ${fl.level === 'warn' ? 'text-red-200/90' : 'text-zinc-400'}`}
                  data-testid={`fp-slot-rule-${i}`}>
                  <span className="font-semibold">{fl.title}</span> — {fl.text}
                  {fl.action && <span className="text-zinc-500"> → {fl.action}</span>}
                </p>
              ))}
            </div>
          )}
          {mode === 'new' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field l="Product *">
                <input list="fp-slot-products" value={f.product_name} data-testid="fp-slot-product"
                  onChange={e => {
                    const name = e.target.value;
                    const hit = products.find(p => p.name === name);
                    setF(x => ({ ...x, product_name: name, product_id: hit?.id || '', pack_size: hit?.pack_size_kg ? `${hit.pack_size_kg} kg` : x.pack_size }));
                  }}
                  className={inputCls} placeholder="e.g. Cheese Seasoning" />
                <datalist id="fp-slot-products">
                  {products.map(p => <option key={p.id} value={p.name} />)}
                </datalist>
              </Field>
              <Field l="Customer / Party (optional)">
                <input value={f.customer_name} onChange={e => set('customer_name', e.target.value)}
                  data-testid="fp-slot-customer" className={inputCls} placeholder="blank = stock production" />
              </Field>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field l="Production date *">
              <input type="date" value={f.planned_date} onChange={e => set('planned_date', e.target.value)}
                data-testid="fp-slot-date" className={inputCls} />
            </Field>
            <Field l="Shift">
              <select value={f.shift} onChange={e => set('shift', e.target.value)}
                data-testid="fp-slot-shift" className={inputCls}>
                {SHIFTS.map(s => <option key={s.v} value={s.v} className="bg-zinc-900">{s.l}</option>)}
              </select>
            </Field>
            <Field l="Line">
              <input value={f.line_no} onChange={e => set('line_no', e.target.value)}
                data-testid="fp-slot-line" className={inputCls} placeholder="1 / 2 / 1+2" />
            </Field>
            <Field l="Priority">
              <select value={f.priority} onChange={e => set('priority', e.target.value)}
                data-testid="fp-slot-priority" className={inputCls}>
                {PRIORITIES.map(p => <option key={p.v} value={p.v} className="bg-zinc-900">{p.l}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field l={`Batch size kg${fh ? '' : ' (Factory Head only)'}`}>
              <input type="number" step="any" value={f.batch_size} disabled={!fh}
                onChange={e => set('batch_size', e.target.value)}
                data-testid="fp-slot-batch-size" className={inputCls} placeholder="from recipe" />
            </Field>
            <Field l="No. of batches">
              <input type="number" value={f.batches} onChange={e => applyBatches(parseInt(e.target.value || 0, 10))}
                data-testid="fp-slot-batches" className={inputCls} />
            </Field>
            <Field l="Total quantity kg *">
              <input type="number" step="any" value={f.quantity} onChange={e => set('quantity', e.target.value)}
                data-testid="fp-slot-qty" className={inputCls} />
            </Field>
            <Field l="Pack size">
              <input value={f.pack_size} disabled={!fh} onChange={e => set('pack_size', e.target.value)}
                data-testid="fp-slot-pack" className={inputCls} placeholder="25/BAG" />
            </Field>
          </div>

          {calc && (
            <div className="bg-amber-500/10 border border-amber-500/25 rounded-xl p-3 text-xs" data-testid="fp-slot-batch-calc">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Kv k="Exact batch requirement" v={`${calc.exact} batches`} />
                <Kv k="Standard full batches" v={`${calc.full}`} />
                <Kv k="Planned output" v={fmtQty(calc.output, 'kg')} tone="text-emerald-300" />
                <Kv k="Excess" v={fmtQty(calc.excess, 'kg')} tone={calc.excess > 0.001 ? 'text-orange-300' : 'text-zinc-400'} />
              </div>
              {calc.excess > 0.001 && (
                <button onClick={() => applyBatches(calc.full)} data-testid="fp-slot-accept-full"
                  className="mt-2 text-[11px] bg-amber-500 hover:bg-amber-400 text-black font-semibold px-2.5 py-1 rounded-lg">
                  Accept {calc.full} full batches → {fmtQty(calc.output, 'kg')}
                </button>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field l="Purpose">
              <select value={f.purpose} onChange={e => set('purpose', e.target.value)}
                data-testid="fp-slot-purpose" className={inputCls}>
                {PURPOSES.map(p => <option key={p.v} value={p.v} className="bg-zinc-900">{p.l}</option>)}
              </select>
            </Field>
            <Field l="Required / delivery date">
              <input type="date" value={f.required_date} onChange={e => set('required_date', e.target.value)}
                data-testid="fp-slot-required-date" className={inputCls} />
            </Field>
          </div>

          <Field l="Production instruction — what should the floor do?">
            <textarea rows={2} value={f.instructions} onChange={e => set('instructions', e.target.value)}
              data-testid="fp-slot-instruction" className={inputCls}
              placeholder="e.g. make less salty · use previous approved batch as reference · do not use old RM lot" />
          </Field>
          <Field l="Remark — what should management remember?">
            <textarea rows={2} value={f.remarks} onChange={e => set('remarks', e.target.value)}
              data-testid="fp-slot-remark" className={inputCls}
              placeholder="e.g. RM expected tomorrow · wash the line before this · hold production" />
          </Field>
        </div>

        <div className="sticky bottom-0 ti-surface-modal border-t border-zinc-800 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="ti-btn-ghost text-xs px-3 py-2 rounded-lg">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="fp-slot-save"
            className="bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-700 text-black text-xs font-semibold px-4 py-2 rounded-lg">
            {saving ? 'Saving…' : mode === 'move' ? 'Move slot' : mode === 'duplicate' ? 'Duplicate' : 'Save slot'}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = 'w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-1.5 text-xs text-zinc-100 focus:outline-none focus:border-amber-500 disabled:opacity-50';

function Field({ l, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">{l}</label>
      {children}
    </div>
  );
}

function Kv({ k, v, tone = 'text-zinc-100' }) {
  return (
    <div>
      <p className="text-[10px] uppercase text-zinc-500 tracking-wider">{k}</p>
      <p className={`text-sm font-mono font-semibold ${tone}`}>{v}</p>
    </div>
  );
}
